// Section 4: Society Expenses & Charges
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Building2 } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";
import { REQ } from "../utils";

export const SocietyExpensesSection = ({
  formData,
  setFormData,
  handleChange,
  handleSelectChange,
  handleSocietyChargeChange,
  getInputClass,
  hasFieldError,
  confirmedZeroFields
}) => {
  // Only render for redevelopment projects
  if (formData.projectMode === "greenfield") {
    return null;
  }

  // Toggle helper — normalise undefined → true so existing projects keep working
  const isOn = (flag) => formData[flag] !== false;
  const toggleFlag = (flag, checked) => setFormData({ ...formData, [flag]: checked });

  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-indigo-400" data-testid="quick-society-expenses">
      <CardHeader className="pb-3 bg-indigo-100 rounded-t-xl border-b border-indigo-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-indigo-900">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          4. Society Expenses & Charges
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Commercial GST Offset — only shown when project has offices/shops */}
        {(formData.hasOffices || formData.hasShops) && formData.projectType !== "residential" && (() => {
          const raw = parseFloat(formData.commercialGSTOffset);
          const offset = isNaN(raw) ? 5 : raw;
          const netRate = Math.max(0, 12 - offset).toFixed(1);
          return (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[180px]">
                <Label htmlFor="q-commercial-gst-offset" className="text-xs font-medium text-indigo-900">
                  Commercial GST Offset (%)
                </Label>
                <Input
                  id="q-commercial-gst-offset"
                  name="commercialGSTOffset"
                  type="number"
                  step="0.5"
                  min="0"
                  max="12"
                  value={formData.commercialGSTOffset ?? "5"}
                  onChange={handleChange}
                  placeholder="5"
                  className="h-8 rounded-sm text-sm"
                  data-testid="quick-commercial-gst-offset"
                />
              </div>
              <p className="text-xs text-indigo-800 leading-relaxed">
                Applies to <strong>Offices &amp; Shops only</strong>. GST = 12% − {offset}% offset = <strong>{netRate}%</strong> net. Flats stay at 5%.
              </p>
            </div>
          );
        })()}

        {/* RENT ROW */}
        <div className={`space-y-3 p-4 bg-secondary/30 rounded-md ${isOn("includeRent") ? "" : "opacity-60"}`}>
          <div className="flex items-center justify-between">
            <Label className="font-medium text-base">Rent</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={isOn("includeRent")}
                onCheckedChange={(c) => toggleFlag("includeRent", c)}
                data-testid="quick-toggle-rent"
              />
              <Label className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                {isOn("includeRent") ? "Included" : "Not applicable"}
              </Label>
            </div>
          </div>
          <fieldset disabled={!isOn("includeRent")} className={isOn("includeRent") ? "" : "pointer-events-none"}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Rent Type</Label>
              <Select
                value={formData.rentType || "per_sft"}
                onValueChange={(value) => handleSelectChange("rentType", value)}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="quick-rent-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_sft">Per Sft</SelectItem>
                  <SelectItem value="per_number">Lumpsum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(formData.rentType || "per_sft") === "per_sft" && formData.hasFlats && (
              <div className="space-y-1">
                <Label className="text-xs">Flat (₹/sft/month) *</Label>
                <Input
                  name="rentPerSqftFlat"
                  type="number"
                  value={formData.rentPerSqftFlat}
                  onChange={handleSocietyChargeChange}
                  placeholder="30"
                  className={`h-9 ${getInputClass("rentPerSqftFlat")}`}
                  data-testid="quick-rent-flat"
                />
                {confirmedZeroFields.rentPerSqftFlat && <p className="text-xs text-amber-600">₹0 confirmed</p>}
              </div>
            )}
            {(formData.rentType || "per_sft") === "per_sft" && formData.hasOffices && formData.projectType !== "residential" && (
              <div className="space-y-1">
                <Label className="text-xs">Office (₹/sft/month)</Label>
                <Input
                  name="rentPerSqftOffice"
                  type="number"
                  value={formData.rentPerSqftOffice}
                  onChange={handleSocietyChargeChange}
                  placeholder="40"
                  className="rounded-sm h-9"
                  data-testid="quick-rent-office"
                />
              </div>
            )}
            {(formData.rentType || "per_sft") === "per_sft" && formData.hasShops && formData.projectType !== "residential" && (
              <div className="space-y-1">
                <Label className="text-xs">Shop (₹/sft/month)</Label>
                <Input
                  name="rentPerSqftShop"
                  type="number"
                  value={formData.rentPerSqftShop}
                  onChange={handleSocietyChargeChange}
                  placeholder="50"
                  className="rounded-sm h-9"
                  data-testid="quick-rent-shop"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Duration (months) *</Label>
              <Input
                name="projectDuration"
                type="number"
                value={formData.projectDuration}
                onChange={handleChange}
                placeholder="36"
                className={`h-9 ${REQ}`}
                data-testid="quick-duration"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Annual Increment (%)</Label>
              <Input
                name="rentIncrementPA"
                type="number"
                value={formData.rentIncrementPA || "5"}
                onChange={handleChange}
                placeholder="5"
                className="rounded-sm h-9"
                data-testid="quick-rent-increment"
              />
            </div>
          </div>
          
          {/* Lumpsum Rent */}
          {formData.rentType === "per_number" && (
            <div className="space-y-3 mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground">
                Enter flat counts (must equal {formData.numMembers || 0} members) and monthly rent per flat type
              </p>
              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">1 BHK Count</Label>
                  <Input name="count1BHK" type="number" value={formData.count1BHK || ""} onChange={handleChange} placeholder="0" className="rounded-sm h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">2 BHK Count</Label>
                  <Input name="count2BHK" type="number" value={formData.count2BHK || ""} onChange={handleChange} placeholder="0" className="rounded-sm h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">3 BHK Count</Label>
                  <Input name="count3BHK" type="number" value={formData.count3BHK || ""} onChange={handleChange} placeholder="0" className="rounded-sm h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">4 BHK Count</Label>
                  <Input name="count4BHK" type="number" value={formData.count4BHK || ""} onChange={handleChange} placeholder="0" className="rounded-sm h-8" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">1 BHK Rent (₹)</Label>
                  <IndianNumberInput name="rent1BHK" value={formData.rent1BHK || ""} onChange={handleChange} placeholder="15,000" className="h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">2 BHK Rent (₹)</Label>
                  <IndianNumberInput name="rent2BHK" value={formData.rent2BHK || ""} onChange={handleChange} placeholder="25,000" className="h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">3 BHK Rent (₹)</Label>
                  <IndianNumberInput name="rent3BHK" value={formData.rent3BHK || ""} onChange={handleChange} placeholder="35,000" className="h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">4 BHK Rent (₹)</Label>
                  <IndianNumberInput name="rent4BHK" value={formData.rent4BHK || ""} onChange={handleChange} placeholder="50,000" className="h-8" />
                </div>
              </div>
            </div>
          )}
          </fieldset>
        </div>
        
        {/* CORPUS ROW */}
        <div className={`space-y-3 p-4 bg-secondary/30 rounded-md ${isOn("includeCorpusCharges") ? "" : "opacity-60"}`}>
          <div className="flex items-center justify-between">
            <Label className="font-medium text-base">Corpus Charges *</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={isOn("includeCorpusCharges")}
                onCheckedChange={(c) => toggleFlag("includeCorpusCharges", c)}
                data-testid="quick-toggle-corpus"
              />
              <Label className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                {isOn("includeCorpusCharges") ? "Included" : "Not applicable"}
              </Label>
            </div>
          </div>
          <fieldset disabled={!isOn("includeCorpusCharges")} className={isOn("includeCorpusCharges") ? "" : "pointer-events-none"}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Charge Type</Label>
              <Select
                value={formData.corpusChargesType || "per_member"}
                onValueChange={(value) => handleSelectChange("corpusChargesType", value)}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="quick-corpus-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_member">Per Member</SelectItem>
                  <SelectItem value="per_sft">Per Sft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Amount {formData.corpusChargesType === "per_sft" ? "(₹/sft)" : "(₹/member)"}</Label>
              <IndianNumberInput
                name="corpusCharges"
                value={formData.corpusCharges}
                onChange={handleSocietyChargeChange}
                placeholder={formData.corpusChargesType === "per_sft" ? "100" : "1,00,000"}
                className={`h-9 ${getInputClass("corpusCharges")}`}
                data-testid="quick-corpus"
              />
              {hasFieldError("corpusCharges") && <p className="text-xs text-red-500">Required</p>}
              {confirmedZeroFields.corpusCharges && <p className="text-xs text-amber-600">₹0 confirmed</p>}
            </div>
          </div>
          </fieldset>
        </div>
        
        {/* BETTERMENT ROW */}
        <div className={`space-y-3 p-4 bg-secondary/30 rounded-md ${isOn("includeBettermentCharges") ? "" : "opacity-60"}`}>
          <div className="flex items-center justify-between">
            <Label className="font-medium text-base">Betterment Charges *</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={isOn("includeBettermentCharges")}
                onCheckedChange={(c) => toggleFlag("includeBettermentCharges", c)}
                data-testid="quick-toggle-betterment"
              />
              <Label className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                {isOn("includeBettermentCharges") ? "Included" : "Not applicable"}
              </Label>
            </div>
          </div>
          <fieldset disabled={!isOn("includeBettermentCharges")} className={isOn("includeBettermentCharges") ? "" : "pointer-events-none"}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Charge Type</Label>
              <Select
                value={formData.bettermentChargesType || "per_member"}
                onValueChange={(value) => handleSelectChange("bettermentChargesType", value)}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="quick-betterment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_member">Per Flat</SelectItem>
                  <SelectItem value="per_sft">Per Sft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Flats {formData.bettermentChargesType === "per_sft" ? "(₹/sft)" : "(₹/flat)"}</Label>
              <IndianNumberInput
                name="bettermentCharges"
                value={formData.bettermentCharges}
                onChange={handleSocietyChargeChange}
                placeholder={formData.bettermentChargesType === "per_sft" ? "100" : "75,000"}
                className={`h-9 ${getInputClass("bettermentCharges")}`}
                data-testid="quick-betterment"
              />
              {hasFieldError("bettermentCharges") && <p className="text-xs text-red-500">Required</p>}
              {confirmedZeroFields.bettermentCharges && <p className="text-xs text-amber-600">₹0 confirmed</p>}
            </div>
            {formData.hasOffices && (
              <div className="space-y-1">
                <Label className="text-xs">Offices</Label>
                <IndianNumberInput
                  name="bettermentChargesOffices"
                  value={formData.bettermentChargesOffices || ""}
                  onChange={handleChange}
                  placeholder="Same as Flats"
                  className="rounded-sm h-9"
                  data-testid="quick-betterment-offices"
                />
              </div>
            )}
            {formData.hasShops && (
              <div className="space-y-1">
                <Label className="text-xs">Shops</Label>
                <IndianNumberInput
                  name="bettermentChargesShops"
                  value={formData.bettermentChargesShops || ""}
                  onChange={handleChange}
                  placeholder="Same as Flats"
                  className="rounded-sm h-9"
                  data-testid="quick-betterment-shops"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Payment Timing</Label>
              <Select
                value={formData.bettermentPaymentTiming || "at_possession"}
                onValueChange={(value) => handleSelectChange("bettermentPaymentTiming", value)}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="quick-betterment-timing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="at_possession">At Possession</SelectItem>
                  <SelectItem value="split">50-50 Split</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          </fieldset>
        </div>
        
        {/* SHIFTING ROW */}
        <div className={`space-y-3 p-4 bg-secondary/30 rounded-md ${isOn("includeShiftingCharges") ? "" : "opacity-60"}`}>
          <div className="flex items-center justify-between">
            <Label className="font-medium text-base">Shifting Charges *</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={isOn("includeShiftingCharges")}
                onCheckedChange={(c) => toggleFlag("includeShiftingCharges", c)}
                data-testid="quick-toggle-shifting"
              />
              <Label className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                {isOn("includeShiftingCharges") ? "Included" : "Not applicable"}
              </Label>
            </div>
          </div>
          <fieldset disabled={!isOn("includeShiftingCharges")} className={isOn("includeShiftingCharges") ? "" : "pointer-events-none"}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Amount (₹/member)</Label>
              <IndianNumberInput
                name="shiftingCharges"
                value={formData.shiftingCharges}
                onChange={handleSocietyChargeChange}
                placeholder="50,000"
                className={`h-9 ${getInputClass("shiftingCharges")}`}
                data-testid="quick-shifting"
              />
              {hasFieldError("shiftingCharges") && <p className="text-xs text-red-500">Required</p>}
              {confirmedZeroFields.shiftingCharges && <p className="text-xs text-amber-600">₹0 confirmed</p>}
            </div>
          </div>
          </fieldset>
        </div>
        
        <Separator />
        
        {/* Parking, PMC & Bank Guarantee */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="q-fourWheelerPerMember">4-Wheeler Parking (per member)</Label>
            <Input
              id="q-fourWheelerPerMember"
              name="fourWheelerPerMember"
              type="number"
              value={formData.fourWheelerPerMember}
              onChange={handleChange}
              placeholder="1"
              className="rounded-sm"
              data-testid="quick-4wheeler"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="q-twoWheelerPerMember">2-Wheeler Parking (per member)</Label>
            <Input
              id="q-twoWheelerPerMember"
              name="twoWheelerPerMember"
              type="number"
              value={formData.twoWheelerPerMember}
              onChange={handleChange}
              placeholder="1"
              className="rounded-sm"
              data-testid="quick-2wheeler"
            />
          </div>
          {formData.projectType !== "residential" && (
            <div className="space-y-2">
              <Label htmlFor="q-shopRentDuration">Shop Rent Duration (months)</Label>
              <Input
                id="q-shopRentDuration"
                name="shopRentDuration"
                type="number"
                value={formData.shopRentDuration || ""}
                onChange={handleChange}
                placeholder="Same as project"
                className="rounded-sm"
                data-testid="quick-shop-rent-duration"
              />
              <p className="text-xs text-muted-foreground">Leave blank = same as project duration</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="q-bankGuaranteeAmount">BG Amount (if applicable)</Label>
            <IndianNumberInput
              id="q-bankGuaranteeAmount"
              name="bankGuaranteeAmount"
              value={formData.bankGuaranteeAmount || ""}
              onChange={handleChange}
              placeholder="10,00,000"
              className="rounded-sm"
              data-testid="quick-bg-amount"
            />
          </div>
        </div>
        
        {/* Refundable Deposit, Brokerage, Society PMC */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Refundable Deposit</Label>
            <Select
              value={formData.refundableDeposit || "no"}
              onValueChange={(value) => handleSelectChange("refundableDeposit", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="quick-deposit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            {formData.refundableDeposit === "yes" && (
              <Select
                value={formData.depositMonths || "1"}
                onValueChange={(value) => handleSelectChange("depositMonths", value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-deposit-months">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="2">2 Months</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>Brokerage for Rent</Label>
            <Select
              value={formData.brokeragePayable || "no"}
              onValueChange={(value) => handleSelectChange("brokeragePayable", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="quick-brokerage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            {formData.brokeragePayable === "yes" && (
              <Select
                value={formData.brokerageMonths || "1"}
                onValueChange={(value) => handleSelectChange("brokerageMonths", value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-brokerage-months">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="2">2 Months</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>Society PMC Charges</Label>
            <Select
              value={formData.societyPMCPayable || "no"}
              onValueChange={(value) => handleSelectChange("societyPMCPayable", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="quick-pmc">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            {formData.societyPMCPayable === "yes" && (
              <IndianNumberInput
                id="q-pmcAmount"
                name="pmcAmount"
                value={formData.pmcAmount}
                onChange={handleChange}
                placeholder="5,00,000"
                className="rounded-sm"
                data-testid="quick-pmc-amount"
              />
            )}
          </div>
        </div>
        
        {/* Agent Commission */}
        <div className="space-y-3">
          <Label className="font-medium">Agent Commission</Label>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Commission Type</Label>
              <Select
                value={formData.brokerageCommissionType || "per_sqft"}
                onValueChange={(value) => handleSelectChange("brokerageCommissionType", value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-brokerage-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_sqft">Per Sq Ft</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(formData.brokerageCommissionType || "per_sqft") === "per_sqft" ? (
              <div className="space-y-2">
                <Label htmlFor="q-brokeragePerSqft">Commission (₹/sft)</Label>
                <Input
                  id="q-brokeragePerSqft"
                  name="brokerageCommissionPerSqft"
                  type="number"
                  value={formData.brokerageCommissionPerSqft || ""}
                  onChange={handleChange}
                  placeholder="50"
                  className="rounded-sm"
                  data-testid="quick-brokerage-persqft"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="q-brokerageFixed">Commission (Fixed ₹)</Label>
                <IndianNumberInput
                  id="q-brokerageFixed"
                  name="brokerageCommissionFixed"
                  value={formData.brokerageCommissionFixed || ""}
                  onChange={handleChange}
                  placeholder="50,00,000"
                  className="rounded-sm"
                  data-testid="quick-brokerage-fixed"
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
